'use client';

import { createDebug } from '@shared/debug';
import type { Address, LoqateFindItem, LoqateRetrieveItem } from '../types';

class LoqateService {
  private debug = createDebug({ isEnabled: false });

  constructor(
    private readonly apiKey: string,
    private readonly baseUrl: string,
    private readonly countries: string = 'GB'
  ) {
    this.baseUrl = baseUrl || 'https://api.addressy.com';
  }
  /**
   * Find addresses using Loqate Find API
   */
  async findAddresses(query: string, containerId?: string): Promise<LoqateFindItem[]> {
    this.throwErrorWhenApiKeyIsMissing();

    try {
      const url = new URL(`${this.baseUrl}/Capture/Interactive/Find/v1.10/json3.ws`);
      url.searchParams.append('Key', this.apiKey);
      url.searchParams.append('Text', query);
      url.searchParams.append('Countries', this.countries);
      url.searchParams.append('Language', 'en');
      url.searchParams.append('Limit', '10');

      // Add Container parameter if provided
      if (containerId) {
        url.searchParams.append('Container', containerId);
      }

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (data.Items && Array.isArray(data.Items)) {
        return data.Items;
      }

      return [];
    } catch (error) {
      this.debug.error('Error finding addresses:', error);
      throw new Error('Failed to fetch address suggestions', { cause: error });
    }
  }

  /**
   * Retrieve full address details using Loqate Retrieve API
   */
  async retrieveAddress(id: string): Promise<Address | null> {
    this.throwErrorWhenApiKeyIsMissing();

    try {
      const url = new URL(`${this.baseUrl}/Capture/Interactive/Retrieve/v1.2/json3.ws`);
      url.searchParams.append('Key', this.apiKey);
      url.searchParams.append('Id', id);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (data.Items && Array.isArray(data.Items) && data.Items.length > 0) {
        const item = data.Items[0] as LoqateRetrieveItem;
        const state = this.countries.includes('AU') ? item.AdminAreaCode || item.ProvinceCode || item.AdminAreaName || item.ProvinceName || undefined : undefined;

        // Transform to our Address format
        const address: Address = {
          id: item.Id,
          line1: item.Line1,
          line2: item.Line2 || undefined,
          city: item.City,
          state,
          postcode: item.PostalCode,
          fullAddress: [item.Line1, item.Line2, item.City, item.PostalCode].filter(Boolean).join(', '),
          buildingName: item.BuildingName || undefined,
          buildingNumber: item.SubBuilding || item.BuildingNumber || undefined,
          street: this.resolveStreet(item),
        };

        return address;
      }

      return null;
    } catch (error) {
      this.debug.error('Error retrieving address:', error);
      throw new Error('Failed to retrieve address details', { cause: error });
    }
  }

  /**
   * When a unit occupies the house field, keep the street number on the street name.
   * Otherwise the street number is discarded because SubBuilding replaces BuildingNumber.
   */
  private resolveStreet(item: LoqateRetrieveItem): string | undefined {
    const street = item.Street?.trim() ?? '';
    const buildingNumber = item.BuildingNumber?.trim() ?? '';
    const subBuilding = item.SubBuilding?.trim() ?? '';

    if (!street) {
      return undefined;
    }

    if (subBuilding && buildingNumber) {
      const alreadyIncludesNumber = street === buildingNumber || street.startsWith(`${buildingNumber} `);
      return alreadyIncludesNumber ? street : `${buildingNumber} ${street}`;
    }

    return street || undefined;
  }

  private throwErrorWhenApiKeyIsMissing() {
    if (this.apiKey) {
      return;
    }

    throw new Error('Loqate API key is missing');
  }
}

// Export the class (but not a singleton instance)
export { LoqateService };
